import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { F7205Service } from './f-7205.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

@Component({
    selector: 'gtw-f-7205-ce',
    templateUrl: './f-7205.component.html',
    styleUrls: ['./f-7205.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F7205Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  baseURLs: GtwElementsBaseUrlsModel = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS
  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component properties from AngularJS controller
  f7205Header: any = {};
  f7205List: any[] = [];
  f7205OriginalData: any[] = [];
  f7205FormData: any[] = [];
  f7205NTList: any[] = [];
  f7205FinalData: any[] = [];
  unAllocatedAmt: number = 0;

  scenario: string = '';
  glbl_issue_id: string = '';
  local_issue_id: string = '';
  bShowSaveButton: boolean = true;
  cur_scenario_type: string = '';

  // Dropdown options - matching AngularJS
  section: string[] = [];
  classification: { [key: string]: string } = {
    'NA': 'NA',
    'Worthless': 'Worthless Stock',
    'Other': 'Other Flow Thru'
  };
  classificationKeys: string[] = ['NA', 'Worthless', 'Other'];

  isValidScenario: boolean = false;
  isExamScenario: boolean = false;

  userSettings: any;
  logged_in_user: string = '';

  constructor(
    private f7205Service: F7205Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.cdr.markForCheck();
  }

  // Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';

      this.scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario || '';
      this.glbl_issue_id = this.globalService?.globalParams?.issue_id || '';

      // Check exam scenario
      this.checkExamScenario();

      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.loadFormData();
      }
    } catch (e) {
      console.error('Error in F7205 init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  checkExamScenario() {
    // Check if scenario type is EXAM
    const scenarioTypeCode = this.globalService?.inputs?.tax_year?.selected?.scenario_type_code;
    this.isExamScenario = scenarioTypeCode === 'EXAM' && this.checkExamScenarioFilter();
  }

  checkExamScenarioFilter(): boolean {
    // This would need to check filters - simplified for now
    return false;
  }

  checkEditScenario(s_type_code: string, t_scenario: string, t_issue: string): boolean {
    if (this.scenario == t_scenario && ((s_type_code == 'EXAM' && this.glbl_issue_id == t_issue) || s_type_code != 'EXAM')) {
      return true;
    }
    return false;
  }

  loadFormData() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    this.f7205Service.fetchF7205Details(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.logged_in_user,
      this.filterParams.scenario || this.scenario,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data && data.callSuccess === '1' && data.jsonObject) {
          this.processFormData(data);
        } else if (data && data.errorMessage) {
          this._snackBar.open(data.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Error loading form data:', error);
        this.showFormLoader = false;
        this.isLoading = false;
        this._snackBar.open('Error loading form data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    });
  }

  private processFormData(data: any) {
    this.f7205Header = data.jsonObject[0];
    this.f7205List = data.jsonObject.slice(1);
    this.f7205NTList = data.jsonObject.filter((item: any) => item.ROW_TYPE === 'NT');

    // Check valid scenario
    for (let i = 0; i < this.f7205List.length; i++) {
      if (!this.isValidScenario && this.f7205List[i].T_SCENARIO_TYPE_CODE) {
        this.isValidScenario = this.checkEditScenario(
          this.f7205List[i].T_SCENARIO_TYPE_CODE,
          this.f7205List[i].T_SCENARIO,
          this.f7205List[i].ISSUE_KEY
        );
      }
    }

    // Process data structure exactly like AngularJS
    const o = this.f7205List.reduce((a: any, b: any) => {
      a[b.LINE_ORDER] = a[b.LINE_ORDER] || [];
      a[b.LINE_ORDER].push(b);
      return a;
    }, {});

    const a = Object.keys(o).map((k: string) => {
      return { ROW_TYPE: o[k][0].ROW_TYPE, DELETE_ROWS: [], Line_data: o[k] };
    });

    this.f7205FormData = a;

    // Process CH rows
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH') {
        if (this.f7205FormData[i].Line_data[0] && this.f7205FormData[i].Line_data[0].OCCURRENCE != null) {
          const z = this.f7205FormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.OCCURRENCE] = a[b.OCCURRENCE] || [];
            a[b.OCCURRENCE].push(b);
            return a;
          }, {});

          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "DATA_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Line_no: z[k][0].LINE_NO,
              IS_EDITABLE: z[k][0].IS_EDITABLE,
              IS_EDITTED: "N",
              Trans_details_key: z[k][0].TRANS_DETAILS_KEY,
              Occurrence: z[k][0].OCCURRENCE,
              Line_data_occ: z[k]
            };
          });

          const m = _.cloneDeep(y);

          for (let x = 0; x < m.length; x++) {
            const d = (2 * x) + 1;
            y.splice(d, 0, _.cloneDeep(m[x]));
            y[d].Line_Type = "DATA_COMMENTS";
            y[d].Line_data_occ = [y[2 * x].Line_data_occ[y[2 * x].Line_data_occ.length - 1]];
          }

          const c = _.cloneDeep(y[0]);
          c.Line_Type = "HEADER_ROW";
          c.Occurrence = "";
          c.Trans_details_key = "";
          c.IS_EDITABLE = "Y";
          for (let j = 0; j < c.Line_data_occ.length; j++) {
            c.Line_data_occ[j].ATTRIB_VALUE = c.Line_data_occ[j].ATTRIB_DESCRIPTION;
          }

          y.unshift(c, { Line_Type: "SUB_ROW", Is_Multi: c.Is_Multi, Line_no: c.Line_no, IS_EDITABLE: "Y", IS_EDITTED: "N", Trans_details_key: "", Occurrence: "", Line_data_occ: [] });
          this.f7205FormData[i].Line_data = _.cloneDeep(y);
        } else {
          const z = this.f7205FormData[i].Line_data.reduce((a: any, b: any) => {
            a[b.LINE_NO] = a[b.LINE_NO] || [];
            a[b.LINE_NO].push(b);
            return a;
          }, {});

          const y = Object.keys(z).map((k: string) => {
            return {
              Line_Type: "HEADER_ROW",
              Is_Multi: z[k][0].IS_MULTI,
              Line_no: z[k][0].LINE_NO,
              IS_EDITABLE: z[k][0].IS_EDITABLE,
              IS_EDITTED: "N",
              Trans_details_key: "",
              Occurrence: "",
              Line_data_occ: z[k]
            };
          });

          for (let j = 0; j < y[0].Line_data_occ.length; j++) {
            y[0].Line_data_occ[j].ATTRIB_VALUE = y[0].Line_data_occ[j].ATTRIB_DESCRIPTION;
          }

          y.push({ Line_Type: "SUB_ROW", Is_Multi: y[0].Is_Multi, Line_no: y[0].Line_no, IS_EDITABLE: "Y", IS_EDITTED: "N", Trans_details_key: "", Occurrence: "", Line_data_occ: [] });
          this.f7205FormData[i].Line_data = y;
        }
      } else if (this.f7205FormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          if (this.f7205FormData[i].Line_data[j].IS_EDITABLE == 'Y' &&
            this.f7205FormData[i].Line_data[j].EDIT_TYPE == 'currency' &&
            this.f7205FormData[i].Line_data[j].DATA_TYPE == 'number') {
            this.f7205FormData[i].Line_data[j].ATTRIB_VALUE = parseInt(this.f7205FormData[i].Line_data[j].ATTRIB_VALUE);
          }
        }
      }
    }

    // Initialize select field values (equivalent to ng-init in AngularJS)
    this.initializeSelectValues();
    
    // Calculate final amounts including Unallocated Amount
    this.calcFinalAmounts();
    
    this.f7205OriginalData = _.cloneDeep(this.f7205FormData);
    this.cdr.detectChanges();
  }

  /**
   * Initialize select field values - equivalent to ng-init in AngularJS
   * For Property Section: Set to first option if current value not in list
   * For Classification Type: Set to 'NA' if null
   */
  private initializeSelectValues() {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE === 'CH') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          const item = this.f7205FormData[i].Line_data[j];
          if (item.Line_data_occ && Array.isArray(item.Line_data_occ)) {
            for (let k = 0; k < item.Line_data_occ.length; k++) {
              const column = item.Line_data_occ[k];
              if (column.EDIT_TYPE === 'select' && column.DATA_TYPE === 'string') {
                // Property Section: Initialize to first option if value not in list
                if (column.ATTRIB_DESCRIPTION === 'Property Section') {
                  if (this.section.indexOf(column.ATTRIB_VALUE) === -1) {
                    column.ATTRIB_VALUE = this.section.length > 0 ? this.section[0] : column.ATTRIB_VALUE;
                  }
                }
                // Classification Type: Initialize to 'NA' if null
                if (column.ATTRIB_DESCRIPTION === 'Classification Type') {
                  if (column.ATTRIB_VALUE == null) {
                    column.ATTRIB_VALUE = 'NA';
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  getUnallocatedAmt() {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'TH') {
        // Search for 'Unallocated Amount' in all Line_data items
        const lineData = this.f7205FormData[i].Line_data;
        if (lineData && lineData.length > 0) {
          for (let j = 0; j < lineData.length; j++) {
            if (lineData[j].DESCRIPTION == 'Unallocated Amount' &&
                lineData[j].DATA_TYPE == 'number' &&
                lineData[j].ATTRIB_VALUE != null &&
                lineData[j].ATTRIB_VALUE != undefined) {
              this.unAllocatedAmt = parseInt(lineData[j].ATTRIB_VALUE) || 0;
              return;
            }
          }
        }
      }
    }
  }

  headerCheckBox(row: any, index: number, event?: any) {
    // Update the value based on checkbox state - convert to 'Y'/'N' for consistency
    const isChecked = event && event.target ? event.target.checked : false;
    row.ATTRIB_VALUE = isChecked ? 'Y' : 'N';
    row.IS_CHANGED_FLAG = 'Y';
    
    const tempAttribShortCode = row.ATTRIB_SHORT_CODE === 'F7205_NA_NA_HeaderQuesY' ? 'F7205_NA_NA_HeaderQuesN' : 'F7205_NA_NA_HeaderQuesY';
    
    if (isChecked) {
      // Ensure only one checkbox is checked - set the other to 'N'
      this.f7205NTList.forEach((item: any, idx: number) => {
        if (item.ATTRIB_SHORT_CODE === tempAttribShortCode) {
          item.ATTRIB_VALUE = 'N';
          item.IS_CHANGED_FLAG = 'Y';
        }
      });
    }
    
    // Also update in f7205FormData if NT rows are stored there
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'NT') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          if (j == index) {
            this.f7205FormData[i].Line_data[j].ATTRIB_VALUE = isChecked ? 'Y' : 'N';
            this.f7205FormData[i].Line_data[j].IS_CHANGED_FLAG = 'Y';
          }
          // Also update the other checkbox in f7205FormData
          if (this.f7205FormData[i].Line_data[j].ATTRIB_SHORT_CODE === tempAttribShortCode) {
            this.f7205FormData[i].Line_data[j].ATTRIB_VALUE = 'N';
            this.f7205FormData[i].Line_data[j].IS_CHANGED_FLAG = 'Y';
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  AddNewRow(line_no: string) {
    let occurrance = 1;
    let trans_details_key: any = null;

    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH') {
        const lastItem = this.f7205FormData[i].Line_data[this.f7205FormData[i].Line_data.length - 1];
        if (lastItem && lastItem.Occurrence != null && lastItem.Occurrence != "" && lastItem.Occurrence != undefined) {
          occurrance = parseInt(lastItem.Occurrence) + 1;
          trans_details_key = lastItem.Trans_details_key;
        }

        const new_row = _.cloneDeep(this.f7205FormData[i].Line_data[0]);
        new_row.Line_Type = "NEW_EDIT_ROW";
        new_row.Occurrence = occurrance;
        new_row.Trans_details_key = trans_details_key;
        new_row.IS_EDITTED = "Y";

        if (new_row.Line_no == line_no) {
          for (let j = 0; j < new_row.Line_data_occ.length; j++) {
            if (new_row.Line_data_occ[j].EDIT_TYPE == "custom_date_various" && new_row.Line_data_occ[j].DATA_TYPE == "dateVarious") {
              new_row.Line_data_occ[j].ATTRIB_VALUE = new Date();
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "currency" && new_row.Line_data_occ[j].DATA_TYPE == "number") {
              new_row.Line_data_occ[j].ATTRIB_VALUE = 0;
            } else if (new_row.Line_data_occ[j].EDIT_TYPE == "label" && new_row.Line_data_occ[j].DATA_TYPE == "string") {
              // Keep as is
            } else if (new_row.Line_data_occ[j].EDIT_TYPE === 'select' && new_row.Line_data_occ[j].DATA_TYPE === 'string') {
              // Initialize select values - equivalent to ng-init
              if (new_row.Line_data_occ[j].ATTRIB_DESCRIPTION === 'Property Section') {
                new_row.Line_data_occ[j].ATTRIB_VALUE = this.section.length > 0 ? this.section[0] : null;
              } else if (new_row.Line_data_occ[j].ATTRIB_DESCRIPTION === 'Classification Type') {
                new_row.Line_data_occ[j].ATTRIB_VALUE = 'NA';
              } else {
                new_row.Line_data_occ[j].ATTRIB_VALUE = null;
              }
            } else {
              new_row.Line_data_occ[j].ATTRIB_VALUE = null;
            }
            new_row.Line_data_occ[j].OCCURRENCE = occurrance;
            new_row.Line_data_occ[j].TRANS_DETAILS_KEY = trans_details_key;
            new_row.Line_data_occ[j].IS_CHANGED_FLAG = "Y";
          }
          this.f7205FormData[i].Line_data.push(new_row);
        }

        const new_row_comments = _.cloneDeep(this.f7205FormData[i].Line_data[0]);
        new_row_comments.Line_Type = "NEW_EDIT_COMMENT_ROW";
        new_row_comments.Occurrence = occurrance;
        new_row_comments.Trans_details_key = trans_details_key;
        if (new_row_comments.Line_no == line_no) {
          new_row_comments.Line_data_occ = [new_row_comments.Line_data_occ[new_row_comments.Line_data_occ.length - 1]];
          new_row_comments.Line_data_occ[0].OCCURRENCE = occurrance;
          new_row_comments.Line_data_occ[0].ATTRIB_VALUE = null;
          new_row_comments.Line_data_occ[0].TRANS_DETAILS_KEY = trans_details_key;
          new_row_comments.Line_data_occ[0].IS_CHANGED_FLAG = "Y";
          this.f7205FormData[i].Line_data.push(new_row_comments);
        }
      }
    }
    this.cdr.detectChanges();
  }

  editExistingRecord(line_no: string, occurrence: any, index: number) {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          if (j == index || j == index + 1) {
            if (this.f7205FormData[i].Line_data[j].Line_Type == "DATA_ROW" &&
              this.f7205FormData[i].Line_data[j].Line_no == line_no &&
              this.f7205FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f7205FormData[i].Line_data[j].Line_Type = "NEW_EDIT_ROW";
              for (let l = 0; l < this.f7205FormData[i].Line_data[j].Line_data_occ.length; l++) {
                if (this.f7205FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "currency" &&
                  this.f7205FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "number") {
                  this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = parseInt(this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
                } else if (this.f7205FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various" &&
                  this.f7205FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious") {
                  if (this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE != "VARIOUS") {
                    this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = new Date(this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
                  }
                }
              }
            } else if (this.f7205FormData[i].Line_data[j].Line_Type == "DATA_COMMENTS" &&
              this.f7205FormData[i].Line_data[j].Line_no == line_no &&
              this.f7205FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f7205FormData[i].Line_data[j].Line_Type = "NEW_EDIT_COMMENT_ROW";
            }
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  removeExistingRecord(line_no: string, Occurrence: any, index: number) {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          if (j == index) {
            if ((this.f7205FormData[i].Line_data[j].Line_Type == "DATA_ROW" ||
              this.f7205FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") &&
              this.f7205FormData[i].Line_data[j].Line_no == line_no &&
              null != this.f7205FormData[i].Line_data[j].Occurrence &&
              this.f7205FormData[i].Line_data[j].Occurrence != "" &&
              this.f7205FormData[i].Line_data[j].Occurrence == Occurrence) {
              this.f7205FormData[i].DELETE_ROWS.push(this.f7205FormData[i].Line_data[j]);
              this.f7205FormData[i].Line_data.splice(j, 2);
            } else if ((this.f7205FormData[i].Line_data[j].Line_Type == "DATA_ROW" ||
              this.f7205FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") &&
              this.f7205FormData[i].Line_data[j].Line_no == line_no &&
              (Occurrence == null || Occurrence == "")) {
              this.f7205FormData[i].Line_data.splice(j, 2);
            }
          }
        }
      }
    }
    this.calcFinalAmounts();
    this.cdr.detectChanges();
  }

  saveNewEdittedRecord(line_no: string, occurrence: any, index: number) {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          if (j == index || j == index + 1) {
            if (this.f7205FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW" &&
              this.f7205FormData[i].Line_data[j].Line_no == line_no &&
              this.f7205FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f7205FormData[i].Line_data[j].Line_Type = "DATA_ROW";
            } else if (this.f7205FormData[i].Line_data[j].Line_Type == "NEW_EDIT_COMMENT_ROW" &&
              this.f7205FormData[i].Line_data[j].Line_no == line_no &&
              this.f7205FormData[i].Line_data[j].Occurrence == occurrence) {
              this.f7205FormData[i].Line_data[j].Line_Type = "DATA_COMMENTS";
            }
          }
          if (j == index) {
            for (let l = 0; l < this.f7205FormData[i].Line_data[j].Line_data_occ.length; l++) {
              if (this.f7205FormData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" &&
                this.f7205FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious" &&
                this.f7205FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various") {
                this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = this.getDateFormat(this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
              } else if (this.f7205FormData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" &&
                this.f7205FormData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "string" &&
                this.f7205FormData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "checkbox") {
                if (this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == null ||
                  this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == undefined) {
                  this.f7205FormData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = "N";
                }
              }
            }
          }
        }
      }
    }
    this.cdr.detectChanges();
  }

  valuechanged(parent_index: number, occurrence: any, line_no: string, index: number, row_type?: string) {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH') {
        if (this.f7205FormData[i].Line_data[parent_index] != null &&
          this.f7205FormData[i].Line_data[parent_index] != undefined) {
          if (this.f7205FormData[i].Line_data[parent_index].Line_Type == "NEW_EDIT_COMMENT_ROW") {
            this.f7205FormData[i].Line_data[parent_index].Line_data_occ[0].IS_CHANGED_FLAG = "Y";
          } else {
            if (this.f7205FormData[i].Line_data[parent_index].Line_no == line_no &&
              this.f7205FormData[i].Line_data[parent_index].Occurrence == occurrence) {
              this.f7205FormData[i].Line_data[parent_index].Line_data_occ[index + 1].IS_CHANGED_FLAG = "Y";
              this.f7205FormData[i].Line_data[parent_index].Line_data_occ[index + 1].HAS_ERROR = "N";
            }
          }
        }
      }
      if (this.f7205FormData[i].ROW_TYPE == 'DT') {
        if (i == parent_index && this.f7205FormData[i].Line_data[index] != null &&
          this.f7205FormData[i].Line_data[index] != undefined) {
          this.f7205FormData[i].Line_data[index].IS_CHANGED_FLAG = "Y";
        }
      }
    }
    this.cdr.detectChanges();
  }

  updateAmount(index: number, line_no: string) {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH' &&
        this.f7205FormData[i].Line_data[0] &&
        this.f7205FormData[i].Line_data[0].Line_no == line_no) {
        for (let j = 0; j < this.f7205FormData[i].Line_data[index].Line_data_occ.length; j++) {
          if (this.f7205FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_CALC_FORMULA != null) {
            const line_data_values = this.f7205FormData[i].Line_data[index].Line_data_occ;
            this.f7205FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_VALUE =
              this.calcLineFormula(
                this.f7205FormData[i].Line_data[index].Line_data_occ[j].ATTRIB_CALC_FORMULA,
                line_data_values
              );
            this.f7205FormData[i].Line_data[index].Line_data_occ[j].IS_CHANGED_FLAG = "Y";
            this.f7205FormData[i].Line_data[index].Line_data_occ[j].HAS_ERROR = "N";
          }
        }
      }
    }
    this.calcFinalAmounts();
    this.cdr.detectChanges();
  }

  calcLineFormula(formula: string, line_data_values: any[]): number {
    let matches = formula.match(/\b[^\d\s]+\b/g);
    let lineFormulaArray: string[];

    if (matches == null) {
      lineFormulaArray = formula.split(/[+-/\\*\\]/);
    } else {
      const formulaLength = formula.length;
      const subFormula = formula.substring(formula.indexOf("("), formulaLength - 1);
      lineFormulaArray = subFormula.split(/[+-,/\\*\\]/);
    }

    for (let i = 0; i < lineFormulaArray.length; i++) {
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        const currentTabAtrribVal = line_data_values.filter((item: any) =>
          item.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
        );
        if (currentTabAtrribVal.length > 0) {
          const lineAmt = parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
          if (parseInt(lineAmt.toString()) > 0) {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
          } else {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt.toString() + ")");
          }
        }
      }
    }

    try {
      const result = safeEvalFormula(formula, null, 'f-7205');
      if (result != null && typeof result !== 'undefined' && parseInt(result.toString()) === parseInt(result.toString())) {
        return parseInt(result.toString());
      }
    } catch (e) {
      console.error('Error in calculation:', e);
    }
    return 0;
  }

  calcFinalAmounts() {
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'TH') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          if (this.f7205FormData[i].Line_data[j].ATTRIB_CALC_FORMULA != null) {
            const formula = this.f7205FormData[i].Line_data[j].ATTRIB_CALC_FORMULA;
            const calculatedValue = this.calc_final_amt(formula);
            this.f7205FormData[i].Line_data[j].ATTRIB_VALUE = calculatedValue;
            this.f7205FormData[i].Line_data[j].IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
    this.getUnallocatedAmt();
    this.cdr.detectChanges();
  }

  calc_final_amt(formula: string): number {
    const lineFormulaArray = formula.split(/[+-/\\*\\]/);
    for (let i = 0; i < lineFormulaArray.length; i++) {
      let lineAmt = 0;
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        for (let k = 0; k < this.f7205FormData.length; k++) {
          if (this.f7205FormData[k].ROW_TYPE == 'CH') {
            for (let j = 0; j < this.f7205FormData[k].Line_data.length; j++) {
              if (this.f7205FormData[k].Line_data[j].Line_Type == "DATA_ROW" ||
                this.f7205FormData[k].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
                const row_data = this.f7205FormData[k].Line_data[j].Line_data_occ;
                const currentTabAtrribVal = row_data.filter((item: any) =>
                  item.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
                );
                if (currentTabAtrribVal.length > 0) {
                  lineAmt = lineAmt + parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
                }
              }
            }
          } else {
            const row_data = this.f7205FormData[k].Line_data;
            const currentTabAtrribVal = row_data.filter((item: any) =>
              item.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
            );
            if (currentTabAtrribVal.length > 0) {
              lineAmt = lineAmt + parseInt(currentTabAtrribVal[0].ATTRIB_VALUE);
            }
          }
        }
        if (parseInt(lineAmt.toString()) > 0) {
          formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
        } else {
          formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt.toString() + ")");
        }
      }
    }

    try {
      const result = safeEvalFormula(formula, null, 'f-7205');
      if (result != null && typeof result !== 'undefined' && parseInt(result.toString()) === parseInt(result.toString())) {
        return parseInt(result.toString());
      }
    } catch (e) {
      console.error('Error in final calculation:', e);
    }
    return 0;
  }

  getDateFormat(date_value: any): string {
    if (date_value == "VARIOUS" || date_value === null || date_value === undefined) {
      return date_value == "VARIOUS" ? "VARIOUS" : "";
    }
    
    // Handle Date object
    if (date_value instanceof Date) {
      const month = (date_value.getMonth() > 8) ? (date_value.getMonth() + 1).toString() : ('0' + (date_value.getMonth() + 1).toString());
      const day = (date_value.getDate() > 9) ? date_value.getDate().toString() : ('0' + date_value.getDate().toString());
      return month + '/' + day + '/' + date_value.getFullYear().toString();
    }
    
    // Handle string date in YYYY-MM-DD format (from HTML date input)
    if (typeof date_value === 'string' && date_value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = date_value.split('-');
      // parts[0] = YYYY, parts[1] = MM, parts[2] = DD
      return parts[1] + '/' + parts[2] + '/' + parts[0]; // MM/DD/YYYY
    }
    
    // Handle string date already in MM/DD/YYYY format
    if (typeof date_value === 'string' && date_value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return date_value; // Already in correct format
    }
    
    return date_value;
  }

  updateFormDataValues() {
    for (let i = 0; i < this.f7205FinalData.length; i++) {
      if (this.f7205FinalData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f7205FinalData[i].Line_data.length; j++) {
          if (this.f7205FinalData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
            for (let l = 0; l < this.f7205FinalData[i].Line_data[j].Line_data_occ.length; l++) {
              if (this.f7205FinalData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" &&
                this.f7205FinalData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "dateVarious" &&
                this.f7205FinalData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "custom_date_various") {
                this.f7205FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE =
                  this.getDateFormat(this.f7205FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE);
              } else if (this.f7205FinalData[i].Line_data[j].Line_data_occ[l].IS_EDITABLE_CELL == "Y" &&
                this.f7205FinalData[i].Line_data[j].Line_data_occ[l].DATA_TYPE == "string" &&
                this.f7205FinalData[i].Line_data[j].Line_data_occ[l].EDIT_TYPE == "checkbox") {
                if (this.f7205FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == null ||
                  this.f7205FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE == undefined) {
                  this.f7205FinalData[i].Line_data[j].Line_data_occ[l].ATTRIB_VALUE = "N";
                }
              }
            }
          }
        }
      }
    }
  }

  validateFormValues(): number {
    let returnValue = 0;
    for (let i = 0; i < this.f7205FormData.length; i++) {
      if (this.f7205FormData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          const line_no = this.f7205FormData[i].Line_data[j].Line_no;

          if (this.f7205FormData[i].Line_data[j].Line_Type == "DATA_ROW" ||
            this.f7205FormData[i].Line_data[j].Line_Type == "NEW_EDIT_ROW") {
            const part_no_match = this.f7205FormData[i].Line_data[j].Line_data_occ[0]?.ATTRIB_SHORT_CODE?.match(/I{1,3}|IV/);
            const part_no = part_no_match ? part_no_match[0] : '';
            const chTable_index = this.f7205FormData[i].Line_data[j].Line_data_occ.findIndex((x: any) =>
              x.ATTRIB_SHORT_CODE == "F7205_N/A_" + part_no + "_" + line_no
            );

            if (chTable_index !== -1) {
              if (this.f7205FormData[i].Line_data[j].Line_data_occ[chTable_index].ATTRIB_VALUE == null ||
                this.f7205FormData[i].Line_data[j].Line_data_occ[chTable_index].ATTRIB_VALUE.length <= 0) {
                this.f7205FormData[i].Line_data[j].Line_data_occ[chTable_index].HAS_ERROR = "Y";
                this.f7205FormData[i].Line_data[j].Line_data_occ[chTable_index].ATTRIB_VALUE_ERROR = "Enter Description of Property for Part " + part_no + ".";
                returnValue = 1;
              }
            }
          }
        }
      } else if (this.f7205FormData[i].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f7205FormData[i].Line_data.length; j++) {
          const line_no = this.f7205FormData[i].Line_data[j].LINE_NO;
          const gain_loss_index = this.f7205FormData[i].Line_data.findIndex((x: any) =>
            x.ATTRIB_SHORT_CODE == "GAIN_LOSS_" + line_no
          );
          if (line_no == "8" && gain_loss_index !== -1 && this.f7205FormData[i].Line_data[gain_loss_index].ATTRIB_VALUE < 0) {
            this.f7205FormData[i].Line_data[j].HAS_ERROR = "Y";
            this.f7205FormData[i].Line_data[j].ATTRIB_VALUE_ERROR = "Please enter a valid amount for Line 8 in Part I.";
            returnValue = 1;
          }
        }
      }
    }
    return returnValue;
  }

  save() {
    const finalData: any[] = [];
    const clob_settings: any[] = [];

    if (this.crudLoading) {
      this._snackBar.open("Please wait while we save this request", 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.f7205FinalData = _.cloneDeep(this.f7205FormData);

    if (_.isEqual(this.f7205FormData, this.f7205OriginalData)) {
      this.cancel();
      return;
    }

    this.updateFormDataValues();
    const returnValue = this.validateFormValues();
    if (returnValue == 1) {
      this._snackBar.open("Please fill all required fields", 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      this.cdr.detectChanges();
      return;
    }

    if (this.unAllocatedAmt != 0) {
      if (!confirm("Allocated Amount is not fully adjusted among Gain or (Loss). Confirm to save the changes.")) {
        return;
      }
    }

    for (let x = 0; x < this.f7205FinalData.length; x++) {
      if (this.f7205FinalData[x].ROW_TYPE == 'CH') {
        this.f7205FinalData[x].Line_data.splice(0, 2);
        if (this.f7205FinalData[x].Line_data.length != null && this.f7205FinalData[x].Line_data.length != undefined) {
          for (let j = 0; j < this.f7205FinalData[x].Line_data.length; j++) {
            this.f7205FinalData[x].Line_data[j].Line_data_occ[this.f7205FinalData[x].Line_data[j].Line_data_occ.length - 1] =
              this.f7205FinalData[x].Line_data[j + 1]?.Line_data_occ[0];
            this.f7205FinalData[x].Line_data.splice(j + 1, 1);
            for (let k = 0; k < this.f7205FinalData[x].Line_data[j].Line_data_occ.length; k++) {
              finalData.push(this.f7205FinalData[x].Line_data[j].Line_data_occ[k]);
            }
          }
        }
        if (this.f7205FinalData[x].DELETE_ROWS.length != null && this.f7205FinalData[x].DELETE_ROWS.length != undefined) {
          for (let j = 0; j < this.f7205FinalData[x].DELETE_ROWS.length; j++) {
            for (let k = 0; k < this.f7205FinalData[x].DELETE_ROWS[j].Line_data_occ.length; k++) {
              this.f7205FinalData[x].DELETE_ROWS[j].Line_data_occ[k].IS_CHANGED_FLAG = "D";
              finalData.push(this.f7205FinalData[x].DELETE_ROWS[j].Line_data_occ[k]);
            }
          }
        }
      }
      if (this.f7205FinalData[x].ROW_TYPE == 'TH' || this.f7205FinalData[x].ROW_TYPE == 'DT') {
        for (let j = 0; j < this.f7205FinalData[x].Line_data.length; j++) {
          finalData.push(this.f7205FinalData[x].Line_data[j]);
        }
      }
      // Include NT rows (header checkbox rows) if they have been changed
      if (this.f7205FinalData[x].ROW_TYPE == 'NT') {
        for (let j = 0; j < this.f7205FinalData[x].Line_data.length; j++) {
          finalData.push(this.f7205FinalData[x].Line_data[j]);
        }
      }
    }

    // Also add any NT rows from f7205NTList that were changed
    for (let i = 0; i < this.f7205NTList.length; i++) {
      if (this.f7205NTList[i].IS_CHANGED_FLAG === 'Y') {
        // Only add if not already included
        const alreadyIncluded = finalData.some((item: any) => 
          item.ATTRIB_SHORT_CODE === this.f7205NTList[i].ATTRIB_SHORT_CODE
        );
        if (!alreadyIncluded) {
          finalData.push(this.f7205NTList[i]);
        }
      }
    }

    clob_settings.push({
      sso_id: this.logged_in_user,
      screen_key: this.globalService?.globalParams?.screen_key || ''
    });

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    this.f7205Service.saveForm7205(
      this.GENERAL_CONFIG.base_url,
      finalData,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage != 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Form 7205 Details are successfully updated.', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    // Reset form to original data without API call (better UX)
    if (this.f7205OriginalData && this.f7205OriginalData.length > 0) {
      // Restore from original data
      this.f7205FormData = _.cloneDeep(this.f7205OriginalData);
      
      // Rebuild NT list from original data
      this.f7205NTList = [];
      for (let i = 0; i < this.f7205FormData.length; i++) {
        if (this.f7205FormData[i].ROW_TYPE === 'NT') {
          this.f7205NTList = this.f7205FormData[i].Line_data;
          break;
        }
      }
      
      // Recalculate unallocated amount
      this.getUnallocatedAmt();
      
      this.cdr.detectChanges();
    } else {
      // No original data, reload from server
      this.isLoading = false;
      this.showFormLoader = true;
      this.cdr.detectChanges();
      this.loadFormData();
    }
  }

  cancel() {
    // If data hasn't loaded yet or no changes, close immediately
    if (!this.f7205FormData || this.f7205FormData.length === 0 || !this.f7205OriginalData || this.f7205OriginalData.length === 0) {
      this.requestGridUpdate.emit('cancel');
      return;
    }

    try {
      const f7205FormJSONParseData = safeJsonInput<any>(JSON.stringify(this.f7205FormData), null, 'f-7205.parse1');
      if (_.isEqual(this.f7205OriginalData, f7205FormJSONParseData)) {
        this.requestGridUpdate.emit('cancel');
      } else {
        if (this.crudLoading) {
          this._snackBar.open("The transaction is still in progress", 'Close', {
            duration: 4000,
            panelClass: ['info-snackbar']
          });
          this.requestGridUpdate.emit('cancel');
        } else if (confirm("Energy efficient deduction is not saved. Do you want to close Form without saving changes?")) {
          this.requestGridUpdate.emit('cancel');
        }
      }
    } catch (e) {
      this.requestGridUpdate.emit('cancel');
    }
  }

  onCheckboxChange(column: any, parentIndex: number, occurrence: any, lineNo: string, colIndex: number, event: any) {
    column.ATTRIB_VALUE = event.target.checked ? 'Y' : 'N';
    this.valuechanged(parentIndex, occurrence, lineNo, colIndex);
  }

  onRadioChange(column: any, parentIndex: number, occurrence: any, lineNo: string, colIndex: number, value: string) {
    column.ATTRIB_VALUE = value;
    this.valuechanged(parentIndex, occurrence, lineNo, colIndex);
  }

  onDateVariousCheckboxChange(column: any, parentIndex: number, occurrence: any, lineNo: string, colIndex: number, event: any) {
    const isChecked = event.target.checked;

    if (isChecked) {
      column.ATTRIB_VALUE = 'VARIOUS';
    } else {
      column.ATTRIB_VALUE = new Date();
    }

    this.valuechanged(parentIndex, occurrence, lineNo, colIndex);
  }

  convertToHtmlDateFormat(date_value: any): string {
    if (date_value == "VARIOUS" || !date_value) {
      return "";
    }

    let dateObj: Date;

    if (date_value instanceof Date) {
      dateObj = date_value;
    } else {
      dateObj = new Date(date_value);
    }

    if (isNaN(dateObj.getTime())) {
      return "";
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Utility function to get difference between dates in days (matching AngularJS)
  getDiffDates(dateAcquired: any, dateSold: any): number {
    if (typeof dateAcquired === 'string') {
      dateAcquired = this.convertToDate(dateAcquired);
      dateSold = this.convertToDate(dateSold);
    }
    return ((dateSold.getTime() - dateAcquired.getTime()) / (1000 * 3600 * 24));
  }

  // Convert string date (MM/DD/YYYY) to Date object (matching AngularJS)
  convertToDate(str: string): Date {
    const dateArr = str.split("/");
    return new Date(parseInt(dateArr[2]), parseInt(dateArr[0]) - 1, parseInt(dateArr[1]));
  }

  // Setup entity data for display (matching AngularJS entityList)
  setupEntityData() {
    // Entity information is displayed directly from rowData in HTML
    // This method can be used for any additional entity data processing if needed
  }
}

